
/**
 * Utility functions for handling HTML content safely
 */

/**
 * Process SVG content to make it safe for use in HTML
 */
export const processSVGContent = (svgContent: string): string => {
  if (!svgContent) return '';
  
  // Remove any script tags for security
  const sanitized = svgContent.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  
  return sanitized;
};

/**
 * Clean HTML content to remove potentially harmful elements
 */
export const cleanHTMLContent = (htmlContent: string): string => {
  if (!htmlContent) return '';
  
  // Simple sanitization - remove script tags
  const sanitized = htmlContent.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  
  return sanitized;
};

/**
 * Safely render HTML content as inner HTML
 */
export const renderHTML = (content: string): { __html: string } => {
  // Return content in format expected by dangerouslySetInnerHTML
  return { __html: cleanHTMLContent(content || '') };
};

/**
 * Create a prompt template for generating questions with canvas/figure elements
 */
export const createFigureQuestionPrompt = (
  questionCount: number,
  subject: string,
  grade: string,
  category?: string,
  difficulty: string = "medium"
): string => {
  return `
Generate ${questionCount} multiple-choice questions for ${grade} grade ${subject}${category ? ` in the category of ${category}` : ''} with ${difficulty} difficulty level. 
These questions should include visual elements using HTML canvas and JavaScript.

For each question:
1. Create a question that requires a visual element (graph, chart, diagram, geometric shape, etc.)
2. Include a properly formatted HTML canvas element in the question text
3. Add a JavaScript code that draws on the canvas to illustrate the question
4. Provide four possible answers (options)
5. Mark exactly one option as correct
6. Provide a brief explanation for the correct answer
7. Set the difficulty field to "${difficulty}"

The canvas and script must be properly structured as follows:
- Include a canvas element with appropriate width and height attributes
- Follow the canvas element with a script element containing JavaScript to draw on the canvas
- Make sure the JavaScript references the canvas using document.getElementById
- The canvas should illustrate the concept being tested
- Ensure the drawing is appropriate for ${grade} grade level

Format the response as a JSON array of questions, where each question has:
- text: the question text including the HTML canvas and script elements
- options: an array of option objects, each with:
  - text: the option text (may include HTML)
  - isCorrect: boolean indicating if this is the correct answer
- explanation: brief explanation of the correct answer (may include HTML)
- difficulty: "${difficulty}"

Example of how the canvas and script should look in a question:
"text": "What shape is shown in the diagram below?<br><canvas id='myCanvas' width='200' height='200'></canvas><script>var canvas = document.getElementById('myCanvas'); var ctx = canvas.getContext('2d'); ctx.beginPath(); ctx.arc(100, 100, 50, 0, 2 * Math.PI); ctx.stroke();</script>"

Make sure all questions are age-appropriate, factually accurate, and directly relevant to the subject and category.
The canvas element MUST be included in the question text.
`;
};

/**
 * Initialize canvas scripts found in the DOM
 * This function looks for canvas elements that have associated script tags
 * and executes the scripts to render content on the canvas
 */
export const initializeCanvasScripts = (): void => {
  console.log("Initializing canvas scripts...");
  
  try {
    // Find all question content containers with data-question-id attribute
    const questionContainers = document.querySelectorAll('.question-content[data-question-id]');
    console.log(`Found ${questionContainers.length} question containers`);
    
    // Process each question container
    questionContainers.forEach((container, containerIndex) => {
      const questionId = container.getAttribute('data-question-id');
      console.log(`Processing question container ${containerIndex + 1}, ID: ${questionId}`);
      
      // Find all canvas elements within this container
      const canvases = container.querySelectorAll('canvas');
      console.log(`Found ${canvases.length} canvas elements in question ${questionId}`);
      
      // Process each canvas element
      canvases.forEach((canvas, canvasIndex) => {
        const canvasId = `canvas-${questionId}-${canvasIndex}`;
        console.log(`Processing canvas ${canvasIndex + 1} in question ${questionId}, setting ID: ${canvasId}`);
        
        // Set a predictable ID to target the canvas
        canvas.id = canvasId;
        
        // Find script element that follows the canvas
        const nextElement = canvas.nextElementSibling;
        
        if (nextElement && nextElement.tagName.toLowerCase() === 'script') {
          console.log(`Found script for canvas ${canvasId}`);
          
          const scriptContent = nextElement.textContent;
          
          if (scriptContent) {
            // Replace getElementById references with this specific canvas ID
            const updatedScript = scriptContent.replace(
              /document\.getElementById\(['"]([^'"]+)['"]\)/g,
              function(match, elemId) {
                console.log(`Replacing element ID reference from ${elemId} to ${canvasId}`);
                return `document.getElementById('${canvasId}')`;
              }
            );
            
            // Execute the script
            try {
              console.log(`Executing script for canvas ${canvasId}`);
              // Using Function constructor instead of eval for better security
              new Function(updatedScript)();
              console.log(`Successfully executed script for canvas ${canvasId}`);
            } catch (e) {
              console.error(`Error executing script for canvas ${canvasId}:`, e);
            }
          }
        } else {
          console.log(`No script found for canvas ${canvasId}`);
        }
      });
    });
    
    console.log("Canvas initialization complete");
  } catch (error) {
    console.error("Error initializing canvas scripts:", error);
  }
};

/**
 * Ensure window.executeScripts is defined for backwards compatibility
 */
export const ensureScriptExecutors = (): void => {
  // Define window.executeScripts function if needed
  if (typeof window !== 'undefined' && !window.executeScripts) {
    window.executeScripts = () => {
      console.warn('window.executeScripts is deprecated, use initializeCanvasScripts instead');
      initializeCanvasScripts();
    };
  }
};

// Initialize script executors when this module is imported
if (typeof window !== 'undefined') {
  ensureScriptExecutors();
}

// Add type declaration to global Window interface
declare global {
  interface Window {
    executeScripts?: () => void;
    initializeCanvases?: () => void;
  }
}
